import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { ReactQueryKeys } from '../../../Constants/ClientRoutingConstants';
import healthDashboardApiService from '../../Dashboards/Health/Utilities/HealthDashboardApiService';
import type { EventFilter } from '../../Events/Utilities/EventsApiService';

interface UseDeviceTimelineQueriesOptions {
  /** Enables notifications + features queries. */
  enabled?: boolean;
  /** Enables event queries (flow, button, temp, pressure, heartbeat, diagnostic). Defaults to `enabled`. */
  eventsEnabled?: boolean;
  keepPreviousData?: boolean;
}

/**
 * Fetches all device timeline data from HealthDashboardApiService.
 * Shared between the asset chart tile and the device health details page.
 */
export function useDeviceTimelineQueries(
  serialNumber: string,
  eventFilter: EventFilter,
  options?: UseDeviceTimelineQueriesOptions,
) {
  const { enabled = true, keepPreviousData = false } = options ?? {};
  const eventsEnabled = options?.eventsEnabled ?? enabled;

  // String key avoids cache churn when the eventFilter object reference changes.
  const rangeKey = `${(eventFilter.startDate as Date)?.getTime?.() ?? ''}:${(eventFilter.endDate as Date)?.getTime?.() ?? ''}`;

  const notificationsQuery = useQuery(
    [ReactQueryKeys.DeviceNotificationsChartQuery, serialNumber, rangeKey],
    () => healthDashboardApiService.getDeviceNotificationsChart(serialNumber, eventFilter),
    { enabled: enabled && !!serialNumber, keepPreviousData },
  );

  const featuresQuery = useQuery(
    [ReactQueryKeys.AllDeviceFeaturesQuery, serialNumber],
    () => healthDashboardApiService.getDeviceFeatures(serialNumber),
    { enabled: enabled && !!serialNumber, keepPreviousData },
  );

  const flowEventsQuery = useQuery(
    ['deviceFlowEvents', serialNumber, rangeKey],
    () => healthDashboardApiService.getDeviceFlowEvents(serialNumber, eventFilter),
    { enabled: eventsEnabled && !!serialNumber, keepPreviousData },
  );

  const buttonEventsQuery = useQuery(
    ['deviceButtonEvents', serialNumber, rangeKey],
    () => healthDashboardApiService.getDeviceButtonEvents(serialNumber, eventFilter),
    { enabled: eventsEnabled && !!serialNumber, keepPreviousData },
  );

  const temperatureEventsQuery = useQuery(
    ['deviceTemperatureEvents', serialNumber, rangeKey],
    () => healthDashboardApiService.getDeviceTemperatureEvents(serialNumber, eventFilter),
    { enabled: eventsEnabled && !!serialNumber, keepPreviousData },
  );

  const pressureEventsQuery = useQuery(
    ['devicePressureEvents', serialNumber, rangeKey],
    () => healthDashboardApiService.getDevicePressureEvents(serialNumber, eventFilter),
    { enabled: eventsEnabled && !!serialNumber, keepPreviousData },
  );

  const heartbeatEventsQuery = useQuery(
    ['deviceHeartbeatEvents', serialNumber, rangeKey],
    () => healthDashboardApiService.getDeviceHeartbeatEvents(serialNumber, eventFilter),
    { enabled: eventsEnabled && !!serialNumber, keepPreviousData },
  );

  const diagnosticEventsQuery = useQuery(
    ['deviceDiagnosticEvents', serialNumber, rangeKey],
    () => healthDashboardApiService.getDeviceDiagnosticEvents(serialNumber, eventFilter),
    { enabled: eventsEnabled && !!serialNumber, keepPreviousData },
  );

  const isBusy =
    notificationsQuery.isFetching ||
    featuresQuery.isFetching ||
    flowEventsQuery.isFetching ||
    buttonEventsQuery.isFetching ||
    temperatureEventsQuery.isFetching ||
    pressureEventsQuery.isFetching ||
    heartbeatEventsQuery.isFetching ||
    diagnosticEventsQuery.isFetching;

  const isLoading =
    notificationsQuery.isLoading ||
    featuresQuery.isLoading ||
    flowEventsQuery.isLoading ||
    buttonEventsQuery.isLoading ||
    temperatureEventsQuery.isLoading ||
    pressureEventsQuery.isLoading ||
    heartbeatEventsQuery.isLoading ||
    diagnosticEventsQuery.isLoading;

  const isError =
    notificationsQuery.isError ||
    featuresQuery.isError ||
    flowEventsQuery.isError ||
    buttonEventsQuery.isError ||
    temperatureEventsQuery.isError ||
    pressureEventsQuery.isError ||
    heartbeatEventsQuery.isError ||
    diagnosticEventsQuery.isError;

  const eventData = useMemo(
    () => ({
      deviceFlowEvents: flowEventsQuery.data,
      deviceButtonEvents: buttonEventsQuery.data,
      deviceTemperatureEvents: temperatureEventsQuery.data,
      devicePressureEvents: pressureEventsQuery.data,
      deviceHeartbeatEvents: heartbeatEventsQuery.data,
      deviceDiagnosticEvents: diagnosticEventsQuery.data,
    }),
    [
      flowEventsQuery.data,
      buttonEventsQuery.data,
      temperatureEventsQuery.data,
      pressureEventsQuery.data,
      heartbeatEventsQuery.data,
      diagnosticEventsQuery.data,
    ],
  );

  return {
    isBusy,
    isLoading,
    isError,
    deviceFeatures: featuresQuery.data,
    deviceNotifications: notificationsQuery.data,
    eventData,
  };
}
